if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        try {
          this.optionMapping = JSON.parse(this.dataset.optionMapping || '{}');
        } catch (e) {
          this.optionMapping = {};
        }

        if (!this.elements.viewer) return;

        if (this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;

        const currentElement = event.detail?.currentElement;
        const mediaId = currentElement?.dataset?.mediaId;
        if (!mediaId) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
      }

      getProductMediaSlides() {
        if (!this.elements.viewer) return [];
        return Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).filter(
          (slide) => !slide.hasAttribute('data-add-on-media-id') && !slide.hasAttribute('data-open-image')
        );
      }

      parseTags(alt) {
        if (!alt) return [];
        const matches = alt.match(/\[([^\]]+)\]/g);
        if (!matches) return [];
        return matches.map((m) => m.slice(1, -1).trim().toLowerCase()).filter(Boolean);
      }

      tagsMatchOptions(tags, selectedOptions) {
        if (!tags.length || !selectedOptions.length) return false;
        const normalizedOptions = selectedOptions.map((o) => o.toLowerCase().trim());

        const categories = {};
        tags.forEach((tag) => {
          const catIndex = this.optionMapping[tag];
          if (catIndex) {
            if (!categories[catIndex]) categories[catIndex] = [];
            categories[catIndex].push(tag);
          }
        });

        const catIndices = Object.keys(categories);
        if (catIndices.length === 0) return false;

        return catIndices.every((catIndex) => {
          const catTags = categories[catIndex];
          const selectedForCat = normalizedOptions[catIndex - 1];
          return catTags.includes(selectedForCat);
        });
      }

      filterMediaByAlt(variantOptions = []) {
        if (!this.elements.viewer) return [];

        const productSlides = this.getProductMediaSlides();

        const anyTaggedSlide = productSlides.some((slide) => {
          const alt = slide.getAttribute('data-alt') || '';
          return this.parseTags(alt).length > 0;
        });

        if (!anyTaggedSlide) {
          productSlides.forEach((slide) => {
            slide.hidden = false;
          });
          if (this.elements.thumbnails) {
            this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
              const linkedSlide = this.elements.viewer.querySelector(
                `[data-media-id="${thumbnail.dataset.target}"]`
              );
              if (linkedSlide && !linkedSlide.hasAttribute('data-add-on-media-id')) {
                thumbnail.hidden = false;
              }
            });
          }
          return [];
        }

        const matchedMediaIds = [];

        productSlides.forEach((slide) => {
          const mediaId = slide.dataset.mediaId;
          const alt = slide.getAttribute('data-alt') || '';
          const tags = this.parseTags(alt);

          if (tags.length === 0) {
            slide.hidden = false;
          } else if (this.tagsMatchOptions(tags, variantOptions)) {
            slide.hidden = false;
            matchedMediaIds.push(mediaId);
          } else {
            slide.hidden = true;
            slide.classList.remove('is-active');
          }
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnail) => {
            const linkedSlide = this.elements.viewer.querySelector(
              `[data-media-id="${thumbnail.dataset.target}"]`
            );
            if (!linkedSlide || linkedSlide.hasAttribute('data-add-on-media-id')) return;
            thumbnail.hidden = linkedSlide.hidden;
          });
        }

        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        if (this.elements.thumbnails?.slider) this.elements.thumbnails.resetPages();

        return matchedMediaIds;
      }

      getFirstVisibleMediaId() {
        const firstVisibleSlide = this.getProductMediaSlides().find((slide) => !slide.hidden);
        return firstVisibleSlide ? firstVisibleSlide.dataset.mediaId : null;
      }

      setActiveMedia(mediaId, prepend) {
        let activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([hidden])`);

        if (!activeMedia) {
          activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
          if (activeMedia && !activeMedia.hasAttribute('data-add-on-media-id')) {
            activeMedia.hidden = false;
            if (this.elements.thumbnails) {
              const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              if (thumbnail) thumbnail.hidden = false;
            }
          }
        }

        if (!activeMedia) {
          const fallbackMediaId = this.getFirstVisibleMediaId();
          if (!fallbackMediaId) return;
          mediaId = fallbackMediaId;
          activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        }

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        if (this.dataset.template?.endsWith('configurator')) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
